import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import type { ApiResponse, BrandSettings, PayloadLocale } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

interface BrandSettingsResponse {
  settings: BrandSettings;
  currency: string;
}

export interface UpdateBrandSettingsDto {
  preferredLanguage?: PayloadLocale;
  currency?: string;
}

export const useUpdateBrandSettingsAction = (brandId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<ApiResponse<BrandSettingsResponse>, Error, UpdateBrandSettingsDto>({
    mutationFn: async (data: UpdateBrandSettingsDto) => {
      const response = await api.put(`/dashboard/brands/${brandId}/settings`, data);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      // Invalidate brand settings query to refetch with new settings
      queryClient.invalidateQueries({ queryKey: ['brandSettings', brandId] });

      // Also invalidate current brand query as it includes settings
      queryClient.invalidateQueries({ queryKey: ['currentBrand'] });

      // Also invalidate brand details for the General Settings page
      queryClient.invalidateQueries({ queryKey: ['brandDetails'] });

      if (variables.currency) {
        toaster.success({
          title: t('settings.brandCurrency.successTitle'),
          description: t('settings.brandCurrency.successDescription'),
        });
      } else {
        toaster.success({
          title: t('settings.brandLanguage.successTitle'),
          description: t('settings.brandLanguage.successDescription'),
        });
      }
    },
    onError: (_error, variables) => {
      toaster.error({
        title: t('common.error'),
        description: variables.currency
          ? t('settings.brandCurrency.errorDescription')
          : t('settings.brandLanguage.errorDescription'),
      });
    },
  });
};
